import { type QueuedRequest, type ApiConfig } from "../../types/api";

export class RequestQueue {
  private queue: QueuedRequest[] = [];
  private config: ApiConfig;
  private processingInterval: NodeJS.Timeout | null = null;

  constructor(config: ApiConfig) {
    this.config = config;
    this.loadQueueFromStorage();
    this.startQueueProcessor();
  }

  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem("api:request_queue");
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch (error) {
      console.warn("Failed to load request queue from storage:", error);
      this.queue = [];
    }
  }

  private saveQueueToStorage(): void {
    try {
      localStorage.setItem("api:request_queue", JSON.stringify(this.queue));
    } catch (error) {
      console.warn("Failed to save request queue to storage:", error);
    }
  }

  public addToQueue(request: QueuedRequest): void {
    if (!this.config.enableQueue) return;

    // Remove duplicate requests
    this.queue = this.queue.filter(
      (qr) => !(qr.url === request.url && qr.method === request.method)
    );

    this.queue.push(request);
    this.queue.sort((a, b) => b.priority - a.priority);
    this.saveQueueToStorage();
  }

  public getQueueLength(): number {
    return this.queue.length;
  }

  public clearQueue(): void {
    this.queue = [];
    this.saveQueueToStorage();
  }

  private startQueueProcessor(): void {
    this.processingInterval = setInterval(() => {
      // The actual processing will be handled by the main ApiService
      // This is just a placeholder for the interval
    }, 5000);
  }

  public async processQueue(
    isOnline: boolean,
    executeRequest: (request: QueuedRequest) => Promise<any>
  ): Promise<void> {
    if (!isOnline || this.queue.length === 0) return;

    const request = this.queue.shift();
    if (!request) return;

    try {
      await executeRequest(request);
      console.log("Queued request processed successfully:", request.url);
      this.saveQueueToStorage();
    } catch (error) {
      console.error("Failed to process queued request:", error);

      // Re-queue with increased retry count if not exceeded
      if (request.retryCount < this.config.retryAttempts) {
        request.retryCount++;
        request.priority -= 10; // Lower priority for retries
        this.addToQueue(request);
      }
    }

    // Continue processing queue
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(isOnline, executeRequest), 1000);
    }
  }

  public destroy(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
  }
}
